import { useMemo, useState } from "react";
import { Search, Download, Sun, LayoutList, Coins, NotebookPen, BookOpen, Library, FileText } from "lucide-react";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { CTASection } from "@/components/CTASection";
import { Input } from "@/components/ui/input";
import { useContent } from "@/context/ContentContext";
import { fileUrl } from "@/lib/api";

const ICONS = { Sun, LayoutList, Coins, NotebookPen, BookOpen, Library };

export default function Resources() {
  const { resources, resourceCategories } = useContent();
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return resources.filter((r) => {
      const matchesCategory = category === "All" || r.category === category;
      const matchesQuery =
        !q || r.title.toLowerCase().includes(q) || r.description.toLowerCase().includes(q);
      return matchesCategory && matchesQuery;
    });
  }, [query, category, resources]);

  return (
    <>
      <section className="container-site pb-14 pt-16 sm:pt-24" data-testid="resources-hero">
        <p className="overline-label">Resources</p>
        <h1 className="mt-5 max-w-3xl font-heading text-4xl font-semibold leading-[1.08] tracking-tight text-foreground sm:text-5xl lg:text-6xl">
          <MaskedLine delay={0.1}>Free tools for</MaskedLine>
          <MaskedLine delay={0.25}>
            <span className="text-terracotta-500">real family life.</span>
          </MaskedLine>
        </h1>
        <Reveal delay={0.4}>
          <p className="mt-7 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">
            Downloadable guides, visual supports, and templates — the same frameworks we use
            with coaching families, free to keep.
          </p>
        </Reveal>
      </section>

      <section className="container-site pb-24" data-testid="resources-library">
        <Reveal>
          <div className="flex flex-col gap-4 rounded-3xl border border-border bg-card p-5 shadow-soft sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                data-testid="resources-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search resources…"
                className="h-12 rounded-full border-input bg-background pl-11"
                aria-label="Search resources"
              />
            </div>
            <div className="flex flex-wrap gap-2" role="group" aria-label="Filter by category">
              {["All", ...resourceCategories].map((c) => (
                <button
                  key={c}
                  data-testid={`resources-filter-${c.toLowerCase().replace(/\s/g, "-")}`}
                  onClick={() => setCategory(c)}
                  className={`rounded-full px-4 py-2 text-xs font-medium transition-colors duration-300 ${
                    category === c
                      ? "bg-forest text-cream dark:bg-terracotta-500"
                      : "border border-border text-muted-foreground hover:border-terracotta-500 hover:text-terracotta-500"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        </Reveal>

        <p className="mt-6 text-sm text-muted-foreground" data-testid="resources-count">
          {filtered.length} {filtered.length === 1 ? "resource" : "resources"}
        </p>

        {filtered.length === 0 ? (
          <div className="mt-10 rounded-3xl border border-dashed border-border p-16 text-center" data-testid="resources-empty">
            <FileText className="mx-auto h-8 w-8 text-muted-foreground" />
            <p className="mt-4 text-sm text-muted-foreground">No resources match your search.</p>
          </div>
        ) : (
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((r, i) => {
              const Icon = ICONS[r.icon] || FileText;
              return (
                <Reveal key={r.id} delay={i * 0.05}>
                  <article
                    data-testid={`resource-card-${r.id}`}
                    className="card-lift flex h-full flex-col rounded-3xl border border-border bg-card p-7 shadow-soft"
                  >
                    {r.thumbnail && (
                      <img src={fileUrl(r.thumbnail)} alt="" className="mb-5 h-36 w-full rounded-2xl object-cover" loading="lazy" />
                    )}
                    <div className="flex items-start justify-between">
                      <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-sage-100 text-forest dark:bg-forest-800 dark:text-sage">
                        <Icon className="h-6 w-6" />
                      </span>
                      <span className={`rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] ${
                        r.tier === "premium"
                          ? "bg-terracotta-100 text-terracotta-600 dark:bg-terracotta-500/15 dark:text-terracotta-400"
                          : "bg-forest/10 text-forest dark:bg-sage/15 dark:text-sage"
                      }`}>
                        {r.tier === "premium" ? "Premium" : "Free"}
                      </span>
                    </div>
                    <p className="mt-6 text-xs font-medium uppercase tracking-[0.18em] text-sage-600 dark:text-sage">{r.category}</p>
                    <h2 className="mt-2 font-heading text-lg font-semibold text-foreground">{r.title}</h2>
                    <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{r.description}</p>
                    {(r.tags || []).length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {r.tags.map((t) => (
                          <span key={t} className="rounded-full bg-muted px-2.5 py-1 text-[10px] font-medium text-muted-foreground">{t}</span>
                        ))}
                      </div>
                    )}
                    <div className="flex-1" />
                    <a
                      href={fileUrl(r.file)}
                      download
                      data-testid={`resource-download-${r.id}`}
                      className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-forest px-5 py-2.5 text-sm font-medium text-cream transition-colors duration-300 hover:bg-terracotta-500 dark:bg-terracotta-500 dark:hover:bg-terracotta-400"
                    >
                      <Download className="h-4 w-4" /> Download PDF
                    </a>
                  </article>
                </Reveal>
              );
            })}
          </div>
        )}
      </section>

      <CTASection
        title="Want strategies tailored to your family?"
        subtitle="Resources are a great start. Coaching makes them yours."
      />
    </>
  );
}
